/**
 * Inspection reports and their remarks. A report (photos or PDF) is read by the AI into its
 * header and remarks, reviewed, then kept in the project; each remark becomes a task with a
 * status, what was done and photos of the fix. The re-inspection pack prints it all for the
 * inspector's second visit. Remark cards are shared with the site survey.
 */
import { useMemo, useState } from 'react';
import type { AiFeature } from './AiUpsell.tsx';
import {
  MAX_REPORT_PAGES,
  reportToOps,
  type AiService,
  type ImageMediaType,
  type InspectionReport,
  isAiUnavailable,
} from '@elec/ai';
import {
  newId,
  revisionsInOrder,
  type Id,
  type Inspection,
  type Op,
  type Project,
  type Remark,
} from '@elec/domain';
import { localeTag, type Locale, type Translate } from '@elec/i18n';
import type { AssetStore } from './assets.ts';
import { PhotoStrip } from './PhotoStrip.tsx';
import { pickReportFiles, prepareReportPages, type Photo } from './photos.ts';
import { TextField } from './surveyUi.tsx';
import { Dialog } from './ui.tsx';

const today = () => new Date().toISOString().slice(0, 10);
const byOrder = (a: Remark, b: Remark) => a.order - b.order || a.id.localeCompare(b.id);

export const inspectionsOf = (p: Project): Inspection[] =>
  Object.values(p.inspections ?? {}).sort((a, b) => b.date.localeCompare(a.date) || a.id.localeCompare(b.id));
export const remarksFor = (p: Project, id: Id): Remark[] =>
  Object.values(p.remarks ?? {})
    .filter((r) => r.inspectionId === id)
    .sort(byOrder);

export interface RemarkCtx {
  t: Translate;
  locale: Locale;
  assets: AssetStore;
  readOnly: boolean;
  notify(msg: string): void;
  dispatch(label: string, ops: Op[]): void;
}

export function RemarkCard({ ctx, remark, n }: { ctx: RemarkCtx; remark: Remark; n: number }) {
  const { t } = ctx;
  const patch = (p: Partial<Remark>) =>
    ctx.dispatch(t('tx.edit'), [{ op: 'update', coll: 'remarks', id: remark.id, patch: p }]);
  const fixed = remark.status === 'fixed';
  return (
    <div className={`rm-card${fixed ? ' fixed' : ''}`} data-testid="remark" data-status={remark.status}>
      <div className="rm-head">
        <strong>{n}.</strong>
        {remark.article && <span className="rm-tag">{remark.article}</span>}
        {remark.ref && <span className="rm-tag mono">{remark.ref}</span>}
        <span className="grow" />
        <button
          className={`rm-status ${fixed ? 'ok' : 'open'}`}
          disabled={ctx.readOnly}
          aria-pressed={fixed}
          onClick={() =>
            patch(fixed ? { status: 'open', fixedOn: undefined } : { status: 'fixed', fixedOn: today() })
          }
          data-testid="remark-status"
        >
          {fixed
            ? `✓ ${t('remark.fixed')}${remark.fixedOn ? ` · ${new Date(`${remark.fixedOn}T12:00:00`).toLocaleDateString(localeTag(ctx.locale))}` : ''}`
            : t('remark.open')}
        </button>
      </div>
      <TextField
        label={t('remark.text')}
        value={remark.text}
        multiline
        onCommit={(text) => patch({ text })}
      />
      <div className="rm-row">
        <TextField
          label={t('remark.article')}
          value={remark.article}
          onCommit={(article) => patch({ article })}
        />
        <TextField
          label={t('remark.ref')}
          value={remark.ref}
          placeholder="B, B3…"
          onCommit={(ref) => patch({ ref: ref.trim().toUpperCase() })}
        />
      </div>
      <TextField
        label={t('remark.fixNote')}
        value={remark.fixNote}
        multiline
        placeholder={t('remark.fixNoteHint')}
        onCommit={(fixNote) => patch({ fixNote })}
        testId="remark-fix"
      />
      <span className="rm-label">{t('remark.photos')}</span>
      <PhotoStrip
        t={t}
        assets={ctx.assets}
        photos={remark.photos}
        onChange={(photos) => patch({ photos })}
        title={t('remark.photos')}
        readOnly={ctx.readOnly}
        notify={ctx.notify}
        testId="remark-photos"
      />
      {!ctx.readOnly && (
        <button
          className="rm-delete"
          onClick={() => ctx.dispatch(t('tx.delete'), [{ op: 'remove', coll: 'remarks', id: remark.id }])}
        >
          {t('remark.delete')}
        </button>
      )}
    </div>
  );
}

/** The report read by the AI, before it goes into the project: remarks can be left out. */
function ReportReview(props: {
  t: Translate;
  report: InspectionReport;
  pages: Photo[];
  demo: boolean;
  onAccept(keep: boolean[]): void;
  onCancel(): void;
}) {
  const { t, report } = props;
  const [keep, setKeep] = useState(() => report.remarks.map(() => true));
  const RESULT: Record<InspectionReport['result'], string> = {
    conform: t('inspection.conform'),
    'not-conform': t('inspection.notConform'),
    unknown: '—',
  };
  return (
    <div className="rm-review" data-testid="report-review">
      {props.demo && <p className="warn">{t('ai.demoBanner')}</p>}
      <dl className="rm-dl">
        <dt>{t('inspection.body')}</dt>
        <dd>{report.body ?? '—'}</dd>
        <dt>{t('inspection.reference')}</dt>
        <dd>{report.reference ?? '—'}</dd>
        <dt>{t('inspection.date')}</dt>
        <dd>{report.date ?? '—'}</dd>
        <dt>{t('inspection.result')}</dt>
        <dd>{RESULT[report.result]}</dd>
        <dt>{t('inspection.deadline')}</dt>
        <dd>{report.deadline ?? '—'}</dd>
      </dl>
      <h4>{t('inspection.remarksFound', { n: report.remarks.length })}</h4>
      <ul className="rm-pick">
        {report.remarks.map((r, i) => (
          <li key={i}>
            <label>
              <input
                type="checkbox"
                checked={keep[i]}
                onChange={(e) => setKeep(keep.map((k, j) => (j === i ? e.target.checked : k)))}
              />
              <span>
                {[r.article, r.ref].filter(Boolean).join(' · ')}
                {(r.article || r.ref) && ' — '}
                {r.text}
              </span>
            </label>
          </li>
        ))}
      </ul>
      {report.notes && <p className="muted small">{report.notes}</p>}
      <div className="row-buttons">
        <button onClick={props.onCancel}>{t('dialog.cancel')}</button>
        <button className="primary" onClick={() => props.onAccept(keep)} data-testid="report-accept">
          {t('inspection.addToProject', { n: keep.filter(Boolean).length })}
        </button>
      </div>
    </div>
  );
}

export function InspectionsDialog(props: {
  project: Project;
  t: Translate;
  locale: Locale;
  ai: AiService;
  /** Runs an AI feature when signed in; otherwise asks to sign in first. */
  withAccount(feature: AiFeature, run: () => void): Promise<void>;
  assets: AssetStore;
  readOnly: boolean;
  notify(msg: string): void;
  dispatch(label: string, ops: Op[]): void;
  ask(r: { title: string; body: string; confirm: string; danger?: boolean }): Promise<boolean>;
  exportPack(inspectionId: Id): void;
  /** Open the remediation quote of this report. */
  onRemediation?(inspectionId: Id): void;
  /** Show on the diagrams what changed since the revision current at the inspection. */
  onSince?(revisionId: Id): void;
  /** Remarks pinned online by the inspection body (review link), not yet taken over. */
  pendingPins?: number;
  onImportPins?(): void;
  onClose(): void;
}) {
  const { project, t, locale } = props;
  const list = useMemo(() => inspectionsOf(project), [project]);
  const [selected, setSelected] = useState<Id | null>(list[0]?.id ?? null);
  const [reading, setReading] = useState<'idle' | 'busy'>('idle');
  const [review, setReview] = useState<{ report: InspectionReport; pages: Photo[] } | null>(null);
  const current = (selected && project.inspections[selected]) || list[0];
  const ctx: RemarkCtx = {
    t,
    locale,
    assets: props.assets,
    readOnly: props.readOnly,
    notify: props.notify,
    dispatch: props.dispatch,
  };

  const read = async () => {
    const files = await pickReportFiles();
    if (!files.length) return;
    setReading('busy');
    try {
      const pages = await prepareReportPages(files, MAX_REPORT_PAGES);
      if (!pages.length) return;
      const report = await props.ai.readInspectionReport(
        pages.map((p) => ({ bytes: p.bytes, mediaType: p.mime as ImageMediaType })),
      );
      setReview({ report, pages });
    } catch (e) {
      props.notify(
        isAiUnavailable(e)
          ? t('ai.unavailable')
          : t('inspection.readFailed', { error: (e as Error).message }),
      );
    } finally {
      setReading('idle');
    }
  };
  const accept = async (keep: boolean[]) => {
    if (!review) return;
    for (const p of review.pages) await props.assets.add(p.id, p.bytes, p.mime, false);
    const { ops, inspectionId } = reportToOps(
      review.report,
      review.pages.map((p) => p.id),
      keep,
    );
    props.dispatch(t('inspection.txAdd'), ops);
    setSelected(inspectionId);
    setReview(null);
  };
  const addManual = () => {
    const value: Inspection = {
      id: newId(),
      date: today(),
      body: '',
      reference: '',
      inspector: '',
      result: 'not-conform',
      pages: [],
      notes: '',
    };
    props.dispatch(t('inspection.txAdd'), [{ op: 'add', coll: 'inspections', value }]);
    setSelected(value.id);
  };
  const patch = (p: Partial<Inspection>) =>
    current &&
    props.dispatch(t('tx.edit'), [{ op: 'update', coll: 'inspections', id: current.id, patch: p }]);
  const remarks = current ? remarksFor(project, current.id) : [];
  const open = remarks.filter((r) => r.status === 'open').length;
  // The revision current on the day of the inspection (the last one made on or before it).
  const sinceRev = current
    ? revisionsInOrder(project)
        .filter((r) => r.date <= current.date)
        .sort((a, b) => a.date.localeCompare(b.date))
        .at(-1)
    : undefined;

  return (
    <Dialog
      title={t('inspection.title')}
      wide
      onClose={props.onClose}
      actions={
        <>
          {current && props.onSince && sinceRev && (
            <button onClick={() => props.onSince!(sinceRev.id)} data-testid="inspection-since">
              {t('inspection.since', { index: sinceRev.index })}
            </button>
          )}
          {current && props.onRemediation && open > 0 && (
            <button onClick={() => props.onRemediation!(current.id)} data-testid="remedy-open">
              {t('remedy.button')}
            </button>
          )}
          {current && (
            <button onClick={() => props.exportPack(current.id)} data-testid="reinspection-pdf">
              {t('inspection.pack')}
            </button>
          )}
          <button className="primary" onClick={props.onClose}>
            {t('dialog.close')}
          </button>
        </>
      }
    >
      {!review && !!props.pendingPins && props.onImportPins && (
        <p className="pins-pending" data-testid="pins-pending">
          {t('inspection.pins', { n: props.pendingPins })}{' '}
          <button className="primary" onClick={props.onImportPins} data-testid="pins-import">
            {t('inspection.importPins')}
          </button>
        </p>
      )}
      {review ? (
        <ReportReview
          t={t}
          report={review.report}
          pages={review.pages}
          demo={props.ai.mode === 'demo'}
          onAccept={(k) => void accept(k)}
          onCancel={() => setReview(null)}
        />
      ) : (
        <div className="rm-layout">
          <div className="rm-toolbar">
            {list.length > 1 && (
              <select
                value={current?.id}
                onChange={(e) => setSelected(e.target.value)}
                aria-label={t('inspection.pick')}
              >
                {list.map((i) => (
                  <option key={i.id} value={i.id}>
                    {[i.date, i.body, i.reference].filter(Boolean).join(' · ')}
                  </option>
                ))}
              </select>
            )}
            <span className="grow" />
            {!props.readOnly && (
              <>
                <button
                  onClick={() => void props.withAccount('report', () => void read())}
                  disabled={reading === 'busy'}
                  data-testid="report-read"
                >
                  {reading === 'busy' ? t('inspection.reading') : t('inspection.read')}
                </button>
                <button onClick={addManual} data-testid="report-manual">
                  {t('inspection.manual')}
                </button>
              </>
            )}
          </div>
          {!current ? (
            <p className="muted">{t('inspection.none')}</p>
          ) : (
            <>
              <div className="rm-header-grid">
                <TextField
                  label={t('inspection.body')}
                  value={current.body}
                  onCommit={(body) => patch({ body })}
                />
                <TextField
                  label={t('inspection.reference')}
                  value={current.reference}
                  onCommit={(reference) => patch({ reference })}
                />
                <label className="sv-field">
                  <span>{t('inspection.date')}</span>
                  <input type="date" value={current.date} onChange={(e) => patch({ date: e.target.value })} />
                </label>
                <TextField
                  label={t('inspection.inspector')}
                  value={current.inspector}
                  onCommit={(inspector) => patch({ inspector })}
                />
                <label className="sv-field">
                  <span>{t('inspection.result')}</span>
                  <select
                    value={current.result}
                    onChange={(e) => patch({ result: e.target.value as Inspection['result'] })}
                  >
                    <option value="not-conform">{t('inspection.notConform')}</option>
                    <option value="conform">{t('inspection.conform')}</option>
                    <option value="unknown">—</option>
                  </select>
                </label>
                <label className="sv-field">
                  <span>{t('inspection.deadline')}</span>
                  <input
                    type="date"
                    value={current.deadline ?? ''}
                    onChange={(e) => patch({ deadline: e.target.value || undefined })}
                  />
                </label>
              </div>
              <span className="rm-label">{t('inspection.pages')}</span>
              <PhotoStrip
                t={t}
                assets={props.assets}
                photos={current.pages}
                onChange={(pages) => patch({ pages })}
                title={t('inspection.pages')}
                readOnly={props.readOnly}
                notify={props.notify}
                noCamera
                testId="report-pages"
              />
              <h4 data-testid="remarks-count">{t('inspection.remarks', { n: remarks.length, open })}</h4>
              {!sinceRev && props.onSince && <p className="muted small">{t('inspection.noRevision')}</p>}
              <div className="rm-list">
                {remarks.map((r, i) => (
                  <RemarkCard key={r.id} ctx={ctx} remark={r} n={i + 1} />
                ))}
              </div>
              {!props.readOnly && (
                <div className="row-buttons">
                  <button
                    onClick={() =>
                      props.dispatch(t('tx.add', { name: t('remark.one') }), [
                        {
                          op: 'add',
                          coll: 'remarks',
                          value: {
                            id: newId(),
                            inspectionId: current.id,
                            order: Math.max(-1, ...remarks.map((r) => r.order)) + 1,
                            text: '',
                            article: '',
                            ref: '',
                            status: 'open',
                            fixNote: '',
                            photos: [],
                          },
                        },
                      ])
                    }
                    data-testid="remark-add"
                  >
                    + {t('remark.add')}
                  </button>
                  <span className="grow" />
                  <button
                    className="danger"
                    onClick={async () => {
                      if (
                        await props.ask({
                          title: t('inspection.delete'),
                          body: t('inspection.deleteConfirm', { n: remarks.length }),
                          confirm: t('inspection.delete'),
                          danger: true,
                        })
                      ) {
                        props.dispatch(t('inspection.delete'), [
                          ...remarks.map((r): Op => ({ op: 'remove', coll: 'remarks', id: r.id })),
                          { op: 'remove', coll: 'inspections', id: current.id },
                        ]);
                        setSelected(null);
                      }
                    }}
                  >
                    {t('inspection.delete')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </Dialog>
  );
}
